import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AlertEvent,
  AlertRule,
  AlertRunResponse,
  AlertTestResponse,
  CreateAlertRuleInput,
  CreateMonitoredUserInput,
  DnsDriftRunResponse,
  DnsHistoryResponse,
  DnsMonitorOverviewResponse,
  DiscoveredDomain,
  DomainSummary,
  DomainSummaryResponse,
  IpDetail,
  Me,
  MonitoredDomainRootsBackfillResponse,
  MonitoredUser,
  OverviewResponse,
  PortfolioResponse,
  ProgressionWizardResponse,
  QuarantineBackfillResponse,
  QuarantineReprocessResult,
  QuarantineRow,
  ReputationCleanupResponse,
  ReputationStatusResponse,
  ReputationSweepResponse,
  SearchResponse,
  SourceRow,
  SubdomainBreakdownResponse,
  AddDomainResponse,
  CreatePlatformTenantInput,
  CreateTenantInput,
  MyTenant,
  PlatformTenantRow,
  TenantSummary,
  TenantUsageResponse,
  UpdatePlatformTenantInput,
  UpdateTenantInput,
  TimeseriesPoint,
  TlsRptSummaryResponse,
  TlsRptUploadOutcome,
  VerifyDomainOutcome,
  UpdateAlertRuleInput,
  UpdateMonitoredUserInput,
  Window,
} from "@rua-watch/shared";
import { apiFetch, apiUrl, ApiError } from "./client";
import { useActiveTenantId } from "./tenant-context";

function useAuthedFetcher() {
  const { getAccessTokenSilently } = useAuth0();
  const tenantId = useActiveTenantId();
  return async <T,>(path: string): Promise<T> => {
    const token = await getAccessTokenSilently();
    return apiFetch<T>(path, { token, tenantId: tenantId ?? undefined });
  };
}

// Escape hatch for the endpoints that don't return JSON (raw R2 streams) or
// send multipart bodies (uploads) — `apiFetch` can't model either. It still
// goes through here so those requests carry the SAME headers as every other
// call, `x-tenant-id` included. Hand-rolling `fetch(apiUrl(…), {authorization})`
// in a component silently drops the active tenant: the Worker then falls back
// to the user's first tenant and answers 404 for rows that belong to the
// workspace actually on screen.
export function useAuthedRawFetch() {
  const { getAccessTokenSilently } = useAuth0();
  const tenantId = useActiveTenantId();
  return useCallback(
    async (path: string, init: RequestInit = {}): Promise<Response> => {
      const token = await getAccessTokenSilently();
      const headers = new Headers(init.headers);
      headers.set("authorization", `Bearer ${token}`);
      if (tenantId != null) headers.set("x-tenant-id", String(tenantId));
      return fetch(apiUrl(path), { ...init, headers });
    },
    [getAccessTokenSilently, tenantId],
  );
}

// Every tenant-scoped query embeds the active tenant id in its key. This is
// what makes the OrgSwitcher work: switching tenant changes the key, so React
// Query fetches fresh data (under the new X-Tenant-Id) instead of serving the
// previous tenant's cached response. Relying on invalidateQueries() alone is a
// race — it runs before the re-render that swaps in the new-tenant fetcher, so
// the refetch goes out with the old header. `null` (no selection) is a valid,
// distinct key meaning "let the server pick the default tenant".
//
// Cross-tenant lists (useMyTenants, usePlatformTenants) intentionally stay
// un-keyed — they don't depend on which tenant is active.

export function useMe() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["me", tenantId],
    queryFn: () => fetcher<Me>("/api/auth/me"),
    staleTime: 5 * 60_000,
  });
}

export function useOverview(domain: string | "all", window: Window) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["overview", tenantId, domain, window],
    queryFn: () =>
      fetcher<OverviewResponse>(`/api/overview?domain=${encodeURIComponent(domain)}&window=${window}`),
  });
}

export function useDomains() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["domains", tenantId],
    queryFn: () => fetcher<DomainSummary[]>("/api/domains"),
  });
}

export function usePortfolio(window: Window) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["portfolio", tenantId, window],
    queryFn: () =>
      fetcher<PortfolioResponse>(`/api/portfolio?window=${window}`),
  });
}

export function useTimeseries(domain: string, window: Window, subdomain?: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  const sub = subdomain ? `&subdomain=${encodeURIComponent(subdomain)}` : "";
  return useQuery({
    queryKey: ["timeseries", tenantId, domain, window, subdomain ?? null],
    queryFn: () =>
      fetcher<TimeseriesPoint[]>(`/api/domains/${encodeURIComponent(domain)}/timeseries?window=${window}${sub}`),
    enabled: !!domain,
  });
}

export function useSources(domain: string, window: Window, subdomain?: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  const sub = subdomain ? `&subdomain=${encodeURIComponent(subdomain)}` : "";
  return useQuery({
    queryKey: ["sources", tenantId, domain, window, subdomain ?? null],
    queryFn: () =>
      fetcher<SourceRow[]>(`/api/domains/${encodeURIComponent(domain)}/sources?window=${window}${sub}`),
    enabled: !!domain,
  });
}

export function useDomainSummary(domain: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["domain-summary", tenantId, domain],
    queryFn: () =>
      fetcher<DomainSummaryResponse>(`/api/domains/${encodeURIComponent(domain)}/summary`),
    enabled: !!domain,
  });
}

// F10 — TLS-RPT manual upload. Multipart POST with the JSON file.
// Reuses the same backend ingest path the email pipeline triggers,
// so a successful round-trip here proves the production pipeline.
export function useUploadTlsRpt() {
  const rawFetch = useAuthedRawFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await rawFetch("/api/tls-rpt/upload", {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        let body: unknown;
        try {
          body = await res.json();
        } catch {
          body = undefined;
        }
        throw new ApiError(res.status, `upload failed (${res.status})`, body);
      }
      return (await res.json()) as TlsRptUploadOutcome;
    },
    onSuccess: () => {
      // The summary card on /domains/:domain reads from the same
      // tls_reports table — invalidate so it picks up the row. Prefix
      // match covers the tenant-scoped key suffix.
      qc.invalidateQueries({ queryKey: ["domain-tlsrpt"] });
    },
  });
}

export function useDomainTlsRpt(domain: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["domain-tlsrpt", tenantId, domain],
    queryFn: () =>
      fetcher<TlsRptSummaryResponse>(`/api/domains/${encodeURIComponent(domain)}/tls-rpt`),
    enabled: !!domain,
  });
}

export function useDomainProgression(domain: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["domain-progression", tenantId, domain],
    queryFn: () =>
      fetcher<ProgressionWizardResponse>(
        `/api/domains/${encodeURIComponent(domain)}/progression`,
      ),
    enabled: !!domain,
  });
}

export function useDomainSubdomains(domain: string, window: Window) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["domain-subdomains", tenantId, domain, window],
    queryFn: () =>
      fetcher<SubdomainBreakdownResponse>(
        `/api/domains/${encodeURIComponent(domain)}/subdomains?window=${window}`,
      ),
    enabled: !!domain,
  });
}

export function useDnsHistory(domain: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["dns-history", tenantId, domain],
    queryFn: () =>
      fetcher<DnsHistoryResponse>(`/api/domains/${encodeURIComponent(domain)}/dns-history`),
    enabled: !!domain,
  });
}

export function useDnsMonitorOverview() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["dns-monitor-overview", tenantId],
    queryFn: () => fetcher<DnsMonitorOverviewResponse>("/api/dns-monitor/overview"),
  });
}

export function useIpDetail(ip: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["ip", tenantId, ip],
    queryFn: () => fetcher<IpDetail>(`/api/ips/${encodeURIComponent(ip)}`),
    enabled: !!ip,
  });
}

// F6 — global search. Disabled by default; SearchPalette enables it
// when the input is non-empty. staleTime keeps results around briefly
// while the user toggles the palette open/closed.
export function useSearch(query: string) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["search", tenantId, query],
    queryFn: () =>
      fetcher<SearchResponse>(`/api/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useQuarantine() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["quarantine", tenantId],
    queryFn: () => fetcher<QuarantineRow[]>("/api/quarantine"),
  });
}

// ---- Admin: monitored_users -----------------------------------------------

function useAuthedMutationFetcher() {
  const { getAccessTokenSilently } = useAuth0();
  const tenantId = useActiveTenantId();
  return async <T,>(path: string, init: { method: string; body?: unknown }): Promise<T> => {
    const token = await getAccessTokenSilently();
    return apiFetch<T>(path, {
      token,
      tenantId: tenantId ?? undefined,
      method: init.method,
      body: init.body,
    });
  };
}

export function useMonitoredUsers() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["admin-users", tenantId],
    queryFn: () => fetcher<MonitoredUser[]>("/api/admin/users"),
  });
}

export function useCreateMonitoredUser() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMonitoredUserInput) =>
      fetcher<MonitoredUser>("/api/admin/users", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
}

export function useUpdateMonitoredUser() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { email: string; input: UpdateMonitoredUserInput }) =>
      fetcher<MonitoredUser>(`/api/admin/users/${encodeURIComponent(args.email)}`, {
        method: "PATCH",
        body: args.input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
}

export function useDeleteMonitoredUser() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      fetcher<{ deleted: true }>(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
}

export interface RefreshEnrichmentResponse {
  enqueued: number;
  mode: "stale" | "all";
  limit: number;
  message?: string;
}

export function useRefreshEnrichment() {
  const fetcher = useAuthedMutationFetcher();
  return useMutation({
    mutationFn: (mode: "stale" | "all") =>
      fetcher<RefreshEnrichmentResponse>(
        `/api/admin/enrichment/refresh?mode=${mode}`,
        { method: "POST" },
      ),
  });
}

export function useRunDnsDrift() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domain?: string) =>
      fetcher<DnsDriftRunResponse>(
        `/api/admin/dns-drift/run${domain ? `?domain=${encodeURIComponent(domain)}` : ""}`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dns-history"] }),
  });
}

export function useBackfillQuarantine() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit: number) =>
      fetcher<QuarantineBackfillResponse>(
        `/api/admin/quarantine/backfill?limit=${limit}`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quarantine"] }),
  });
}

// Re-run quarantined payloads through ingest (e.g. after a parser fix). With
// no args it reprocesses `parse_failed` rows; pass `{ ids }` to target
// specific rows. Rows that now ingest/dedup leave quarantine.
export function useReprocessQuarantine() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids?: number[]; reason?: string; limit?: number } = {}) =>
      fetcher<QuarantineReprocessResult>("/api/admin/quarantine/reprocess", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quarantine"] }),
  });
}

export function useBackfillMonitoredDomainRoots() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetcher<MonitoredDomainRootsBackfillResponse>(
        `/api/admin/monitored-domains/backfill-roots`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

// F4 — alert rules + events.

export function useAlertRules() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["alert-rules", tenantId],
    queryFn: () => fetcher<AlertRule[]>("/api/alerts/rules"),
  });
}

export function useAlertEvents(limit = 200) {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["alert-events", tenantId, limit],
    queryFn: () => fetcher<AlertEvent[]>(`/api/alerts/events?limit=${limit}`),
  });
}

export function useCreateAlertRule() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAlertRuleInput) =>
      fetcher<AlertRule>("/api/admin/alerts/rules", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-rules"] }),
  });
}

export function useUpdateAlertRule() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; input: UpdateAlertRuleInput }) =>
      fetcher<AlertRule>(`/api/admin/alerts/rules/${args.id}`, {
        method: "PATCH",
        body: args.input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-rules"] }),
  });
}

export function useDeleteAlertRule() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetcher<{ deleted: true }>(`/api/admin/alerts/rules/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-rules"] }),
  });
}

export function useTestAlertRule() {
  const fetcher = useAuthedMutationFetcher();
  return useMutation({
    mutationFn: (id: number) =>
      fetcher<AlertTestResponse>(`/api/admin/alerts/rules/${id}/test`, { method: "POST" }),
  });
}

export function useRunAlertCycle() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetcher<AlertRunResponse>("/api/admin/alerts/run", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-events"] }),
  });
}

export function useAcknowledgeAlertEvent() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetcher<{ acknowledged: boolean }>(`/api/alerts/events/${id}/ack`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-events"] }),
  });
}

export function useRunReputationSweep() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit: number) =>
      fetcher<ReputationSweepResponse>(
        `/api/admin/reputation/sweep?limit=${limit}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      qc.invalidateQueries({ queryKey: ["ip"] });
    },
  });
}

export function useReputationStatus() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["reputation-status", tenantId],
    queryFn: () => fetcher<ReputationStatusResponse>("/api/admin/reputation/status"),
    staleTime: 60_000,
  });
}

export function useCleanupReputation() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetcher<ReputationCleanupResponse>("/api/admin/reputation/cleanup", {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      qc.invalidateQueries({ queryKey: ["ip"] });
    },
  });
}

// v3 / R1 — self-service onboarding hooks.

export function useActiveTenant() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["tenant-me", tenantId],
    queryFn: () => fetcher<TenantSummary>("/api/tenants/me"),
    staleTime: 5 * 60_000,
    // 404 = the user has no tenant yet, which is the signal the
    // onboarding wizard waits on. React Query treats throws as errors,
    // so the wizard reads `error` to drive its "create org" CTA.
    retry: false,
  });
}

export function useCreateTenant() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTenantInput) =>
      fetcher<TenantSummary>("/api/tenants", { method: "POST", body: input }),
    onSuccess: () => {
      // tenant-me is keyed by active tenant id (which is null/unset during
      // onboarding and changes once the new workspace is selected), so
      // invalidate by prefix rather than seeding an exact key.
      qc.invalidateQueries({ queryKey: ["tenant-me"] });
      // The /api/auth/me payload also carries activeTenantId/tenantRole,
      // so invalidate so the SPA's role gates pick up the new
      // membership without a full reload.
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["my-tenants"] });
    },
  });
}

export function useAddDomain() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domain: string) =>
      fetcher<AddDomainResponse>("/api/domains", { method: "POST", body: { domain } }),
    onSuccess: () => {
      // The new domain shows up in the listing as soon as it's added,
      // even before it's verified — verification gates report ingest,
      // not visibility.
      qc.invalidateQueries({ queryKey: ["domains"] });
      qc.invalidateQueries({ queryKey: ["discovered"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

export function useVerifyDomain() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domain: string) =>
      fetcher<VerifyDomainOutcome>(
        `/api/domains/${encodeURIComponent(domain)}/verify`,
        { method: "POST" },
      ),
    onSuccess: (outcome) => {
      if (outcome.verified) {
        // On claim the domain leaves the discovered tray and (after the
        // async backfill) shows up in the active listing.
        qc.invalidateQueries({ queryKey: ["domains"] });
        qc.invalidateQueries({ queryKey: ["discovered"] });
        qc.invalidateQueries({ queryKey: ["portfolio"] });
      }
    },
  });
}

// v5 — the "discovered domains" tray: unclaimed roots whose reports are held.
export function useDiscoveredDomains() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["discovered", tenantId],
    queryFn: () => fetcher<DiscoveredDomain[]>("/api/domains/discovered"),
  });
}

// v5 — remove a domain (and everything under it). Dismisses a discovered
// domain OR removes an active one.
export function useDeleteDomain() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domain: string) =>
      fetcher<{ deleted: boolean; domain: string }>(
        `/api/domains/${encodeURIComponent(domain)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["domains"] });
      qc.invalidateQueries({ queryKey: ["discovered"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

// v3 / R1.6 — workspace administration hooks.

// Workspaces the current user belongs to (or every active workspace
// when the user is a platform admin). Drives the OrgSwitcher. NOT keyed
// by active tenant — the list is the same regardless of which workspace
// is currently selected.
export function useMyTenants() {
  const fetcher = useAuthedFetcher();
  return useQuery({
    queryKey: ["my-tenants"],
    queryFn: () => fetcher<MyTenant[]>("/api/tenants"),
    staleTime: 60_000,
  });
}

export function useUpdateTenant() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTenantInput) =>
      fetcher<TenantSummary>("/api/tenants/me", { method: "PATCH", body: input }),
    onSuccess: () => {
      // Prefix-invalidate so the active-tenant-keyed entry refetches.
      qc.invalidateQueries({ queryKey: ["tenant-me"] });
      qc.invalidateQueries({ queryKey: ["my-tenants"] });
    },
  });
}

export function useDeleteTenant() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetcher<{ deleted: boolean; hardDeleteAfter: number }>("/api/tenants/me", { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-me"] });
      qc.invalidateQueries({ queryKey: ["my-tenants"] });
    },
  });
}

// v5 — per-tenant usage / consumption. Keyed by active tenant so the
// OrgSwitcher refetches on tenant change, like the other tenant-scoped
// reads.
export function useTenantUsage() {
  const fetcher = useAuthedFetcher();
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ["tenant-usage", tenantId],
    queryFn: () => fetcher<TenantUsageResponse>("/api/tenants/usage"),
  });
}

// Platform-admin (cross-tenant) hooks. The endpoints 403 for non-admins
// — the SPA gates the route entry on `me.isPlatformAdmin` so we don't
// even try. NOT keyed by active tenant — it's the cross-tenant list.
export function usePlatformTenants() {
  const fetcher = useAuthedFetcher();
  return useQuery({
    queryKey: ["platform-tenants"],
    queryFn: () => fetcher<PlatformTenantRow[]>("/api/platform/tenants"),
  });
}

export function useCreatePlatformTenant() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePlatformTenantInput) =>
      fetcher<PlatformTenantRow>("/api/platform/tenants", { method: "POST", body: input }),
    onSuccess: (row) => {
      // Prepend into the cached list so the new workspace shows without a
      // round-trip, then invalidate so counts/order reconcile with server.
      qc.setQueryData<PlatformTenantRow[] | undefined>(["platform-tenants"], (prev) =>
        prev ? [...prev, row] : prev,
      );
      qc.invalidateQueries({ queryKey: ["platform-tenants"] });
      // A platform admin sees every active tenant in the OrgSwitcher.
      qc.invalidateQueries({ queryKey: ["my-tenants"] });
    },
  });
}

export function useUpdatePlatformTenant() {
  const fetcher = useAuthedMutationFetcher();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdatePlatformTenantInput }) =>
      fetcher<PlatformTenantRow>(`/api/platform/tenants/${id}`, {
        method: "PATCH",
        body: input,
      }),
    onSuccess: (row) => {
      qc.setQueryData<PlatformTenantRow[] | undefined>(["platform-tenants"], (prev) =>
        prev ? prev.map((r) => (r.id === row.id ? row : r)) : prev,
      );
      qc.invalidateQueries({ queryKey: ["tenant-me"] });
      qc.invalidateQueries({ queryKey: ["my-tenants"] });
    },
  });
}
